import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowRight,
  Boxes,
  Check,
  Crosshair,
  Footprints,
  Info,
  Loader2,
  Lock,
  Search,
  Undo2,
  X,
} from 'lucide-react';
import { api, ErroHttp } from '@/lib/api';
import { useCorredores, useProduto } from '@/hooks/consultas';
import { useOrigens } from '@/hooks/negativos';
import { cn, compararNatural, numero } from '@/lib/utils';
import type { VagaMapa } from '@/lib/tipos';
import { acaoTexto, Botao } from '@/componentes/ui/botao';
import { EstadoErro } from '@/componentes/ui/estados';
import { Campo, Esqueleto, Pilula } from '@/componentes/ui/primitivos';
import { ImagemProduto } from '@/componentes/produto/ImagemProduto';
import {
  comoAlcancar,
  comoEndereco,
  DICA_ALCANCE,
  idChip,
  lerEndereco,
  locDoId,
  RackRua,
  useVagaSobCursor,
  type ModoArraste,
} from './rack';

/**
 * Transferência entre vagas — UMA tela, UM desenho.
 *
 * A tela imita a tarefa física, na ordem em que ela acontece no CD:
 * PEGAR numa vaga → LEVAR até outro ponto do galpão → GUARDAR numa vaga.
 * O roteiro no topo diz o que muda o trabalho no chão: em que ANDAR está a
 * vaga (piso = na mão, andar 1-2 = paleteira, andar 3+ = empilhadeira) e se o
 * percurso fica na mesma rua ou obriga a atravessar para outra.
 *
 * Essa nota de alcance é INFORMATIVA e nunca bloqueia: altura de vaga não é
 * regra de negócio, é aviso de logística. O único gate é a simulação de saldo.
 *
 * A DIREÇÃO do fluxo precisa estar escrita na tela, não subentendida. O Paulo
 * leu ao contrário — "a origem eu já tenho, preciso apenas tratar o destino",
 * chamando de origem o negativo que ele clicou. Compensar é o inverso: o
 * lançamento ENTRA no negativo, que portanto é o destino, e o que falta
 * escolher é de onde sai. Ele está certo no essencial, porém: falta UMA
 * escolha só, e por isso a tela já abre com a sugestão aplicada.
 *
 * E o limite do que a tela pode afirmar, que o Paulo apontou depois: **o
 * sistema não sabe o que existe fisicamente na vaga** — só lê o saldo do
 * Kardex. Saldo negativo quer dizer que o lançamento tirou de lá o que o
 * SALDO não tinha; pode haver mercadoria na posição. Então a tela fala de
 * lançamento (saída/entrada), nunca de "está vazio". A informação de andar,
 * equipamento e distância continua útil quando a mercadoria de fato precisa
 * andar, e aparece rotulada como tal — não como afirmação sobre o físico.
 *
 * Por que a origem tem de ter saldo: é regra do PBS
 * (`Usp_ValidaTransferenciaLocalizador` recusa saída sem saldo), não uma
 * afirmação sobre o estoque físico.
 *
 * DUAS regras que a tela tem de deixar óbvias, porque a versão anterior errou
 * nas duas e o Paulo travou:
 *
 *   - **Origem**: só vaga que TEM este SKU. Não dá para tirar do nada. Por
 *     isso elas ficam listadas em cima, sempre visíveis, além de marcadas no
 *     desenho — numa rua de 200 módulos, achar a única vaga com o SKU rolando
 *     o rack era impossível ("não tô conseguindo transferir em lugar nenhum").
 *   - **Destino**: QUALQUER vaga. A vermelha é só a sugerida, porque foi o
 *     negativo que abriu a tela. Guardar numa vaga livre ou numa verde com
 *     espaço é movimento normal de CD ("eu não posso transferir em localizador
 *     verde que tem espaço?" — pode, e agora dá).
 *
 * E como o destino pode ser em outra rua, o arraste PAGINA: levando a carga
 * até a borda direita do desenho, a tela rola a rua e, chegando ao fim dela,
 * troca para a rua seguinte; pela borda esquerda, volta uma rua. Sem soltar a
 * carga no caminho.
 *
 * Regra do PBS que a tela não pode violar: SEMPRE dentro do mesmo centro — a
 * procedure recebe um único `@CENTRO_ESTOQUE` para as duas pernas.
 */

/** Zona de solte do cartão 3 (o destino sugerido), sempre visível no topo. */
const ID_SUGERIDO = 'destino-sugerido';
const ID_ALVO_VAGA = 'alvo:vaga';
const ehAlvoArrastado = (id: string): boolean => id === ID_ALVO_VAGA;
interface PosicaoResolvida {
  localizador: number;
  endereco: string;
  corredor: string;
  produtos: { sku: number; descricao: string; quantidade: number }[];
}

interface Simulacao {
  ok: boolean;
  impedimentos: string[];
  avisos: string[];
  saldoDestinoAntes: number;
  saldoDestinoDepois: number;
  maximoPorEspaco: number | null;
}

export interface PropsTransferencia {
  aberto: boolean;
  centro: number;
  produto: number;
  descricaoProduto: string;
  destino: { localizador: number; endereco: string; saldo: number };
  faltante: number;
  aoFechar: () => void;
  aoConcluir: () => void;
}

interface Candidata {
  localizador: number;
  endereco: string;
  corredor: string;
  saldo: number;
  sugerida: boolean;
}

/** Para onde vai. Começa no negativo que abriu a tela, mas pode ser trocado. */
interface Destino {
  localizador: number;
  endereco: string;
  /** Saldo DESTE SKU. Só é conhecido para o negativo que abriu a tela. */
  saldoSku: number | null;
  /** % de ocupação volumétrica da vaga, quando o CD está medido. */
  pct: number | null;
  /** Saldo total da posição — é o que diz se a vaga está livre. */
  saldoPosicao: number | null;
  ehNegativo: boolean;
}

export function TransferenciaArrastar(props: PropsTransferencia): JSX.Element {
  return (
    <Dialog.Root open={props.aberto} onOpenChange={(v) => !v && props.aoFechar()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-900/55 backdrop-blur-[3px] data-[state=open]:animate-aparecer" />
        <Dialog.Content className="fixed inset-2 z-50 flex flex-col overflow-hidden rounded-2xl border border-borda bg-fundo shadow-forte data-[state=open]:animate-escalar sm:inset-4">
          {props.aberto ? <Conteudo {...props} /> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Conteudo({
  centro,
  produto,
  descricaoProduto,
  destino: negativo,
  faltante,
  aoConcluir,
}: PropsTransferencia): JSX.Element {
  const origens = useOrigens(centro, produto);
  const [ruaVista, definirRuaVista] = React.useState<string | null>(null);
  const [manual, definirManual] = React.useState('');
  const [extras, definirExtras] = React.useState<Candidata[]>([]);
  const [erroManual, definirErroManual] = React.useState<string | null>(null);
  const [origem, definirOrigem] = React.useState<Candidata | null>(null);
  const [origemSugerida, definirOrigemSugerida] = React.useState(false);

  /** Escolha manual: apaga o carimbo de sugestão. */
  const escolherOrigem = React.useCallback((c: Candidata) => {
    definirOrigemSugerida(false);
    definirOrigem(c);
  }, []);
  const [arrastando, definirArrastando] = React.useState<string | null>(null);
  const [quantidade, definirQuantidade] = React.useState('');

  const destinoSugerido = React.useMemo<Destino>(
    () => ({
      localizador: negativo.localizador,
      endereco: comoEndereco(negativo.endereco),
      saldoSku: negativo.saldo,
      pct: null,
      saldoPosicao: null,
      ehNegativo: true,
    }),
    [negativo],
  );
  const [destino, definirDestino] = React.useState<Destino>(destinoSugerido);

  const { sobreLoc, sobreRef } = useVagaSobCursor(arrastando !== null);
  /** Vagas desenhadas agora, por localizador — para montar o destino no drop. */
  const vagasNaTela = React.useRef(new Map<number, VagaMapa>());

  // A rua do negativo é a que o desenho abre primeiro: é o buraco que o
  // operador veio tratar.
  const posNegativo = useQuery<PosicaoResolvida>({
    queryKey: ['resolver', centro, negativo.localizador],
    queryFn: () =>
      api.get<PosicaoResolvida>('/api/localizador/resolver', {
        centro,
        q: String(negativo.localizador),
      }),
  });
  const ruaNegativo = posNegativo.data?.corredor ?? null;

  const sugeridas: Candidata[] = React.useMemo(
    () =>
      (origens.data?.origens ?? []).map((o) => ({
        localizador: o.localizador,
        endereco: comoEndereco(o.endereco),
        corredor: o.corredor,
        saldo: o.saldo,
        sugerida: true,
      })),
    [origens.data],
  );

  const candidatas = React.useMemo(() => {
    const vistos = new Set(extras.map((e) => e.localizador));
    return [...extras, ...sugeridas.filter((s) => !vistos.has(s.localizador))];
  }, [extras, sugeridas]);

  React.useEffect(() => {
    if (!origem) return;
    const padrao = destino.ehNegativo ? Math.max(1, faltante) : origem.saldo;
    definirQuantidade(String(Math.min(origem.saldo, padrao)));
  }, [origem, faltante, destino.ehNegativo]);

  /**
   * TODAS as ruas do centro, em ordem natural — não só as que têm o SKU.
   * O destino pode ser qualquer vaga vaga do CD, então a navegação (seletor e
   * paginação por arraste) precisa alcançar o CD inteiro.
   */
  const corredores = useCorredores(centro);
  const ruas = React.useMemo(() => {
    const contagem = new Map<string, number>();
    for (const c of candidatas) {
      if (c.corredor) contagem.set(c.corredor, (contagem.get(c.corredor) ?? 0) + 1);
    }
    const doCentro = (corredores.data?.corredores ?? []).map((c) => c.corredor);
    const nomes = [
      ...new Set([...doCentro, ...contagem.keys(), ...(ruaNegativo ? [ruaNegativo] : [])]),
    ].sort(compararNatural);
    return nomes.map((nome) => ({
      nome,
      comSku: contagem.get(nome) ?? 0,
      ehDoNegativo: nome === ruaNegativo,
    }));
  }, [candidatas, ruaNegativo, corredores.data]);

  const indiceRua = ruaVista === null ? -1 : ruas.findIndex((r) => r.nome === ruaVista);
  const ruaAnterior = indiceRua > 0 ? (ruas[indiceRua - 1]?.nome ?? null) : null;
  const ruaSeguinte =
    indiceRua >= 0 && indiceRua < ruas.length - 1 ? (ruas[indiceRua + 1]?.nome ?? null) : null;
  const paginarRua = React.useCallback(
    (direcao: -1 | 1): void => {
      const alvo = direcao > 0 ? ruaSeguinte : ruaAnterior;
      if (alvo) definirRuaVista(alvo);
    },
    [ruaAnterior, ruaSeguinte],
  );

  React.useEffect(() => {
    if (ruaVista === null && ruaNegativo !== null) definirRuaVista(ruaNegativo);
  }, [ruaNegativo, ruaVista]);

  /**
   * A tela já abre resolvida.
   *
   * Só falta UMA escolha — de onde tirar — então ela vem preenchida com a
   * mesma regra do painel de negativos: a MENOR posição que cobre o buraco
   * sozinha (esvaziar a pequena preserva os lotes grandes para o
   * abastecimento). Sem origem suficiente, pega a de maior saldo. Trocar é um
   * arraste ou um clique.
   */
  const jaSugeriu = React.useRef(false);
  React.useEffect(() => {
    if (jaSugeriu.current || candidatas.length === 0) return;
    jaSugeriu.current = true;
    const cobrem = candidatas.filter((c) => c.saldo >= faltante);
    const escolha =
      cobrem.length > 0
        ? cobrem.reduce((m, c) => (c.saldo < m.saldo ? c : m))
        : candidatas.reduce((m, c) => (c.saldo > m.saldo ? c : m));
    definirOrigem(escolha);
    definirOrigemSugerida(true);
  }, [candidatas, faltante]);

  const buscar = useMutation({
    mutationFn: (texto: string) =>
      api.get<PosicaoResolvida>('/api/localizador/resolver', { centro, q: texto }),
    onSuccess: (p) => {
      definirErroManual(null);
      const doSku = p.produtos.find((x) => x.sku === produto);
      // Sem o SKU a posição não serve de ORIGEM — mas serve de DESTINO, e é
      // isso que a tela oferece em vez de só recusar.
      if (!doSku || doSku.quantidade <= 0) {
        definirRuaVista(p.corredor);
        definirDestino({
          localizador: p.localizador,
          endereco: comoEndereco(p.endereco),
          saldoSku: 0,
          pct: null,
          saldoPosicao: null,
          ehNegativo: p.localizador === negativo.localizador,
        });
        definirErroManual(
          `${p.endereco} não tem saldo deste SKU, então não pode ser a origem — foi definida como DESTINO.`,
        );
        definirManual('');
        return;
      }
      const nova: Candidata = {
        localizador: p.localizador,
        endereco: comoEndereco(p.endereco),
        corredor: p.corredor,
        saldo: doSku.quantidade,
        sugerida: false,
      };
      definirExtras((atual) => [nova, ...atual.filter((e) => e.localizador !== p.localizador)]);
      definirRuaVista(p.corredor);
      definirManual('');
      escolherOrigem(nova);
    },
    onError: (e) => {
      definirErroManual(e instanceof ErroHttp ? e.message : 'Não foi possível achar a posição.');
    },
  });

  const sensores = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  /** localizador -> saldo do SKU, no formato que o desenho consome. */
  const saldoPorVaga = React.useMemo(
    () => new Map(candidatas.map((c) => [c.localizador, c.saldo])),
    [candidatas],
  );

  const registrarVagas = React.useCallback((m: Map<number, VagaMapa>) => {
    vagasNaTela.current = m;
  }, []);

  const destinoDaVaga = (loc: number, vaga?: VagaMapa): Destino | null => {
    const v = vaga ?? vagasNaTela.current.get(loc);
    if (!v) return null;
    return {
      localizador: v.localizador,
      endereco: comoEndereco(v.endereco),
      saldoSku: loc === negativo.localizador ? negativo.saldo : null,
      pct: v.pct,
      saldoPosicao: v.saldo,
      ehNegativo: loc === negativo.localizador,
    };
  };

  /**
   * O arraste vale nos dois sentidos e nunca é gesto perdido:
   *   vaga com SKU → qualquer vaga  = origem e destino de uma vez;
   *   vaga com SKU → fora           = só define a origem, destino continua;
   *   negativo     → vaga com SKU   = define a origem, destino volta a ser ele.
   */
  const aoSoltar = (evento: DragEndEvent): void => {
    const ativo = String(evento.active.id);
    const solto = sobreRef.current;
    const noCartao = evento.over ? String(evento.over.id) === ID_SUGERIDO : false;
    definirArrastando(null);

    if (ehAlvoArrastado(ativo)) {
      const c = solto !== null ? candidatas.find((x) => x.localizador === solto) : null;
      if (c) {
        escolherOrigem(c);
        definirDestino(destinoSugerido);
      }
      return;
    }

    const c = candidatas.find((x) => x.localizador === locDoId(ativo));
    if (!c) return;
    escolherOrigem(c);

    if (noCartao) {
      definirDestino(destinoSugerido);
      return;
    }
    if (solto !== null && solto !== c.localizador) {
      const d = destinoDaVaga(solto);
      if (d) definirDestino(d);
    }
  };

  const cartaoArrastado =
    arrastando !== null && !ehAlvoArrastado(arrastando)
      ? (candidatas.find((c) => c.localizador === locDoId(arrastando)) ?? null)
      : null;

  /**
   * O que está na mão muda o que pode receber, e a tela tem de mostrar isso:
   *   'carga'    — levando produto: QUALQUER vaga serve de destino;
   *   'negativo' — buscando de onde tirar: só vaga que tenha este SKU.
   * A segunda não é restrição de lugar, é falta de estoque para tirar.
   */
  const modoArraste: ModoArraste =
    arrastando === null ? null : cartaoArrastado ? 'carga' : 'busca';

  return (
    <DndContext
      sensors={sensores}
      collisionDetection={pointerWithin}
      onDragStart={(e: DragStartEvent) => definirArrastando(String(e.active.id))}
      onDragEnd={aoSoltar}
      onDragCancel={() => definirArrastando(null)}
    >
      <header className="flex items-start gap-3 border-b border-borda bg-superficie px-4 py-2.5">
        <FichaProduto sku={produto} descricao={descricaoProduto} />
        <Pilula tom="neutro">
          <Lock className="h-3 w-3" />
          mesmo centro ({centro})
        </Pilula>
        <Dialog.Close asChild>
          <Botao variante="fantasma" tamanho="icone" aria-label="Fechar">
            <X className="h-5 w-5" />
          </Botao>
        </Dialog.Close>
      </header>

      {/* A direção do fluxo, dita de uma vez: sem isto a tela deixa o
          operador supor, e supor errado custa uma movimentação torta. */}
      <div className="border-b border-borda bg-primaria-suave px-4 py-1.5">
        <p className="flex flex-wrap items-center gap-x-1.5 text-[12px] leading-snug">
          <ArrowDownToLine className="h-3.5 w-3.5 shrink-0 text-primaria" />
          <span className="font-mono font-bold">{comoEndereco(negativo.endereco)}</span>
          <span>
            está com <b className="text-status-divergencia">saldo negativo</b> de{' '}
            <b className="tabular">{numero(negativo.saldo)}</b> un. Compensar é lançar{' '}
            <b>saída</b> numa posição com saldo deste SKU e <b>entrada</b> aqui — por isso esta é o{' '}
            <b>destino</b>. Falta escolher só uma coisa: de onde sai.
          </span>
        </p>
        <p className="mt-0.5 text-[11px] leading-snug text-textoFraco">
          O sistema lê o <b>saldo</b>, não o físico da vaga. Se o negativo veio de erro de
          lançamento, isto corrige só o sistema e ninguém precisa ir ao CD; se a mercadoria está
          mesmo em outro lugar, o roteiro abaixo é o caminho dela.
        </p>
      </div>

      <Roteiro
        origem={origem}
        destino={destino}
        destinoSugerido={destinoSugerido}
        ruaNegativo={ruaNegativo}
        quantidade={Number(quantidade) || 0}
        faltante={faltante}
        origemSugerida={origemSugerida}
        emArraste={cartaoArrastado}
        aoTrocarOrigem={() => definirOrigem(null)}
        aoVoltarDestino={() => definirDestino(destinoSugerido)}
      />

      {/* As posições que TÊM o SKU. Ficam aqui em cima, sempre à mão: são as
          únicas origens possíveis, e caçá-las dentro de uma rua de 200 módulos
          era o que travava o operador. */}
      <TiraCandidatas
        candidatas={candidatas}
        origem={origem}
        ruaVista={ruaVista}
        carregando={origens.isLoading}
        produto={produto}
        aoEscolher={escolherOrigem}
        aoVerRua={definirRuaVista}
      />

      <div className="flex flex-wrap items-center gap-2 border-b border-borda bg-superficie px-3 py-2">
        {ruas.length > 0 ? (
          <span className="flex items-center gap-1.5">
            <span className="rotulo">Rua no desenho</span>
            <select
              value={ruaVista ?? ''}
              onChange={(e) => definirRuaVista(e.target.value)}
              aria-label="Rua mostrada no desenho"
              className="min-h-[34px] rounded-lg border border-borda bg-superficie px-2 text-[12.5px] font-semibold shadow-xs"
            >
              {ruas.map((r) => (
                <option key={r.nome} value={r.nome}>
                  {r.nome}
                  {r.ehDoNegativo ? ' · rua do negativo' : ''}
                  {r.comSku > 0 ? ` · ${r.comSku} com o SKU` : ''}
                </option>
              ))}
            </select>
          </span>
        ) : null}

        <form
          className="flex items-center gap-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            const t = manual.trim();
            if (t.length > 0) buscar.mutate(t);
          }}
        >
          <span className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-textoSuave" />
            <Campo
              value={manual}
              onChange={(e) => definirManual(e.target.value)}
              placeholder="Código ou endereço"
              aria-label="Buscar posição por código ou endereço"
              className="min-h-[34px] w-[210px] pl-8 text-[12.5px]"
            />
          </span>
          <Botao
            type="submit"
            variante="secundaria"
            tamanho="pequeno"
            carregando={buscar.isPending}
            disabled={manual.trim().length === 0}
          >
            Usar
          </Botao>
        </form>


      </div>

      {erroManual ? (
        <p className="flex items-start gap-2 border-b border-status-atencao/30 bg-status-atencaoFundo px-3 py-2 text-[12px] text-textoFraco">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-atencao" />
          {erroManual}
          <button
            type="button"
            className={cn(acaoTexto, 'ml-auto shrink-0')}
            onClick={() => definirErroManual(null)}
          >
            ok
          </button>
        </p>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto bg-superficie">
        {origens.isLoading || posNegativo.isLoading ? (
          <div className="p-4">
            <Esqueleto className="h-64" />
          </div>
        ) : posNegativo.isError ? (
          <EstadoErro erro={posNegativo.error} />
        ) : (
          <RackRua
            centro={centro}
            corredor={ruaVista}
            comSku={saldoPorVaga}
            origem={origem?.localizador ?? null}
            destino={destino.localizador}
            alerta={{ localizador: negativo.localizador, saldoSku: negativo.saldo }}
            modoArraste={modoArraste}
            ruaAnterior={ruaAnterior}
            ruaSeguinte={ruaSeguinte}
            aoPaginarRua={paginarRua}
            sobreLoc={sobreLoc}
            registrarVagas={registrarVagas}
            aoClicarVaga={(v, saldo) => {
              if (saldo !== null) {
                escolherOrigem({
                  localizador: v.localizador,
                  endereco: comoEndereco(v.endereco),
                  corredor: ruaVista ?? lerEndereco(v.endereco).corredor,
                  saldo,
                  sugerida: false,
                });
                return;
              }
              const d = destinoDaVaga(v.localizador, v);
              if (d) definirDestino(d);
            }}
            dica={
              <>
                <b className="text-texto">Solte em qualquer vaga</b> — livre, com espaço, a que
                for. Ela vira o destino. A <b className="text-texto">saída</b> só pode sair de
                onde há saldo: as de <b className="text-primaria">borda azul</b>. A{' '}
                <b className="text-status-divergencia">vermelha</b> é o negativo que abriu a tela,
                o destino sugerido. Para guardar em <b className="text-texto">outra rua</b>,
                arraste até a borda do desenho e segure — direita avança, esquerda volta.
              </>
            }
          />
        )}
      </div>

      {origem ? (
        <BarraConfirmacao
          centro={centro}
          produto={produto}
          origem={origem}
          destino={destino}
          quantidade={quantidade}
          definirQuantidade={definirQuantidade}
          aoLimpar={() => definirOrigem(null)}
          aoConcluir={aoConcluir}
        />
      ) : null}

      {/* `width/height: auto`: por padrão o dnd-kit dá ao overlay o tamanho do
          nó arrastado — e como aqui o nó é uma vaga de 32px, o cartão da carga
          saía espremido numa coluna ilegível. */}
      {/* `pointerEvents: none` no WRAPPER, não só no cartão de dentro.
          O wrapper do dnd-kit fica exatamente sob o cursor, e sem isso ele
          respondia ao `elementFromPoint` no lugar da vaga: o arraste começava,
          o cartão seguia o mouse, e soltar não fazia nada. */}
      <DragOverlay
        dropAnimation={null}
        style={{ width: 'auto', height: 'auto', pointerEvents: 'none' }}
      >
        {cartaoArrastado ? (
          <div className="gradiente-marca pointer-events-none flex -translate-y-[118%] items-center gap-2 rounded-xl px-3 py-2 text-white shadow-forte">
            <Boxes className="h-5 w-5" />
            <span className="leading-tight">
              <span className="block text-[11px] font-semibold uppercase tracking-wide opacity-80">
                levando {numero(Math.min(cartaoArrastado.saldo, Math.max(1, faltante)))} un
              </span>
              <span className="block font-mono text-[12.5px] font-bold">
                {cartaoArrastado.endereco}
              </span>
              <span className="block text-[10.5px] opacity-80">
                solte em QUALQUER vaga — ela vira o destino
              </span>
            </span>
          </div>
        ) : arrastando !== null ? (
          <div className="pointer-events-none flex -translate-y-[118%] items-center gap-2 rounded-xl bg-status-divergencia px-3 py-2 text-white shadow-forte">
            <Crosshair className="h-5 w-5" />
            <span className="leading-tight">
              <span className="block text-[11px] font-semibold uppercase tracking-wide opacity-80">
                buraco de {numero(faltante)} un
              </span>
              <span className="block font-mono text-[12.5px] font-bold">{negativo.endereco}</span>
              <span className="block text-[10.5px] opacity-80">
                procurando a saída: solte numa vaga com saldo deste SKU
              </span>
            </span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

// ---------------------------------------------------------------------------
// Tira das posições que podem ser origem
// ---------------------------------------------------------------------------

function TiraCandidatas({
  candidatas,
  origem,
  ruaVista,
  carregando,
  produto,
  aoEscolher,
  aoVerRua,
}: {
  candidatas: readonly Candidata[];
  origem: Candidata | null;
  ruaVista: string | null;
  carregando: boolean;
  produto: number;
  aoEscolher: (c: Candidata) => void;
  aoVerRua: (rua: string) => void;
}): JSX.Element {
  if (carregando) {
    return (
      <div className="border-b border-borda bg-superficie2 p-2.5">
        <Esqueleto className="h-12" />
      </div>
    );
  }
  if (candidatas.length === 0) {
    return (
      <p className="border-b border-borda bg-status-atencaoFundo px-3 py-2 text-[12.5px] text-textoFraco">
        Nenhuma posição deste centro tem saldo do SKU {produto} — não há de onde lançar a saída.
      </p>
    );
  }
  return (
    <div className="border-b border-borda bg-superficie2 px-3 py-2">
      <p className="rotulo mb-1.5">
        De onde pode sair · {candidatas.length}{' '}
        {candidatas.length === 1 ? 'posição tem' : 'posições têm'} saldo deste SKU
      </p>
      {/* Ate 100 posicoes podem ceder saldo; a tira rola em vez de empurrar o
          desenho para fora da tela. */}
      <div className="flex max-h-[96px] flex-wrap gap-1.5 overflow-y-auto">
        {candidatas.map((c) => (
          <ChipCandidata
            key={c.localizador}
            candidata={c}
            escolhida={origem?.localizador === c.localizador}
            naRuaAberta={c.corredor === ruaVista}
            aoEscolher={aoEscolher}
            aoVerRua={aoVerRua}
          />
        ))}
      </div>
    </div>
  );
}

function ChipCandidata({
  candidata,
  escolhida,
  naRuaAberta,
  aoEscolher,
  aoVerRua,
}: {
  candidata: Candidata;
  escolhida: boolean;
  naRuaAberta: boolean;
  aoEscolher: (c: Candidata) => void;
  aoVerRua: (rua: string) => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: idChip(candidata.localizador),
  });
  return (
    <span
      className={cn(
        'flex items-stretch overflow-hidden rounded-lg border shadow-xs transition-transform',
        escolhida
          ? 'border-status-livre bg-status-livreFundo ring-2 ring-status-livre'
          : 'border-primaria/35 bg-primaria-suave',
        isDragging ? 'scale-105 opacity-60' : 'hover:-translate-y-0.5',
      )}
    >
      <button
        ref={setNodeRef}
        type="button"
        {...attributes}
        {...listeners}
        onClick={() => aoEscolher(candidata)}
        title="Clique para lançar a saída daqui, ou arraste até a vaga de entrada"
        className="flex cursor-grab items-center gap-2 px-2.5 py-1.5 text-left active:cursor-grabbing"
      >
        <span className="leading-tight">
          <span className="block font-mono text-[12px] font-bold">{candidata.endereco}</span>
          <span
            className={cn(
              'block text-[10px]',
              escolhida ? 'font-bold text-status-livre' : 'text-textoFraco',
            )}
          >
            {escolhida ? 'ORIGEM ESCOLHIDA' : `${numero(candidata.saldo)} un disponíveis`}
          </span>
        </span>
      </button>
      {!naRuaAberta && candidata.corredor ? (
        <button
          type="button"
          onClick={() => aoVerRua(candidata.corredor)}
          title={`Mostrar a rua ${candidata.corredor} no desenho`}
          className="border-l border-borda px-2 text-[10px] font-bold text-primaria hover:bg-primaria-suave2"
        >
          ver rua
        </button>
      ) : null}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Roteiro da tarefa
// ---------------------------------------------------------------------------

function Roteiro({
  origem,
  destino,
  destinoSugerido,
  ruaNegativo,
  quantidade,
  faltante,
  origemSugerida,
  emArraste,
  aoTrocarOrigem,
  aoVoltarDestino,
}: {
  origem: Candidata | null;
  destino: Destino;
  destinoSugerido: Destino;
  ruaNegativo: string | null;
  quantidade: number;
  faltante: number;
  /** A origem veio do sistema, não do operador — e ele precisa saber disso. */
  origemSugerida: boolean;
  /** Vaga sendo arrastada agora, para o destino reagir ao vivo. */
  emArraste: Candidata | null;
  aoTrocarOrigem: () => void;
  aoVoltarDestino: () => void;
}): JSX.Element {
  const go = origem ? lerEndereco(origem.endereco) : null;
  const gd = lerEndereco(destino.endereco);
  const alcanceOrigem = comoAlcancar(go?.andar ?? null);
  const alcanceDestino = comoAlcancar(gd.andar);

  const ruaO = origem?.corredor || go?.corredor || '—';
  const ruaD = (destino.ehNegativo ? ruaNegativo : null) ?? gd.corredor ?? '—';
  const mesmaRua = go !== null && ruaO === ruaD;
  const modO = Number(go?.modulo);
  const modD = Number(gd.modulo);
  const distancia =
    mesmaRua && Number.isFinite(modO) && Number.isFinite(modD) ? Math.abs(modO - modD) : null;

  const previsao =
    emArraste && destino.saldoSku !== null
      ? destino.saldoSku + Math.min(emArraste.saldo, Math.max(1, faltante))
      : null;

  const { setNodeRef, isOver } = useDroppable({ id: ID_SUGERIDO });
  const { attributes, listeners, setNodeRef: refArraste } = useDraggable({ id: ID_ALVO_VAGA });
  const trocado = destino.localizador !== destinoSugerido.localizador;

  return (
    <div className="flex flex-wrap items-stretch gap-2 border-b border-borda bg-superficie2 px-3 py-2">
      <Passo
        numero={1}
        rotulo={origemSugerida ? 'Pegar em · sugestão' : 'Pegar em'}
        ativo={origem !== null}
        tom="origem"
      >
        {origem && go ? (
          <>
            <span className="block font-mono text-[14px] font-bold leading-none">
              {origem.endereco}
            </span>
            {origemSugerida ? (
              <span className="mt-0.5 block text-[10.5px] font-semibold text-primaria">
                escolhida pelo sistema — arraste outra vaga para trocar
              </span>
            ) : null}
            <span
              title={DICA_ALCANCE}
              className="mt-1 flex items-center gap-1.5 text-[11px] text-textoFraco"
            >
              <alcanceOrigem.Icone className="h-3.5 w-3.5" />
              {alcanceOrigem.texto}
            </span>
            <span className="mt-0.5 block text-[11px] text-textoFraco">
              rua {ruaO} · módulo {go.modulo} · tem{' '}
              <b className="tabular text-texto">{numero(origem.saldo)}</b> un
            </span>
            {origem.saldo < faltante ? (
              // Dizer isso na cara: ha negativos onde nenhuma posicao cobre o
              // buraco sozinha (SKU 33703 no 2201: buraco de 142, maior origem
              // com 4), e sem o aviso parece que a tela resolveu quando nao.
              <span className="mt-0.5 block text-[10.5px] font-semibold text-status-atencao">
                cobre {numero(origem.saldo)} dos {numero(faltante)} un — sozinha não zera; o painel
                de negativos soma várias origens de uma vez
              </span>
            ) : null}
          </>
        ) : (
          <span className="text-[12px] italic text-textoSuave">
            escolha na tira acima, ou arraste uma vaga com saldo deste SKU — o PBS recusa saída
            sem saldo
          </span>
        )}
      </Passo>

      <Passo
        numero={2}
        rotulo="Levar · se for movimentação física"
        ativo={origem !== null}
        tom="neutro"
      >
        {origem && go ? (
          <>
            <span className="flex items-center gap-1.5 text-[12.5px] font-bold leading-none">
              <Footprints className="h-4 w-4 text-primaria" />
              {mesmaRua ? `dentro da rua ${ruaD}` : `rua ${ruaO} → rua ${ruaD}`}
            </span>
            <span className="mt-1 block text-[11px] text-textoFraco">
              {distancia !== null
                ? `${distancia} ${distancia === 1 ? 'módulo' : 'módulos'} de distância`
                : 'percurso entre ruas do CD'}
            </span>
            <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-textoFraco">
              <Boxes className="h-3.5 w-3.5" />
              carregando <b className="tabular text-texto">{numero(quantidade)}</b> un
            </span>
          </>
        ) : (
          <span className="text-[12px] italic text-textoSuave">o percurso aparece aqui</span>
        )}
      </Passo>

      {/* Enquanto algo está na mão, o destino ABRE: cresce, ganha borda
          tracejada e diz o que acontece se soltar ali. */}
      <div
        ref={(el) => {
          setNodeRef(el);
          refArraste(el);
        }}
        {...attributes}
        {...listeners}
        className={cn(
          'flex min-w-[240px] flex-1 cursor-grab items-start gap-2.5 rounded-xl border-2 px-3 py-2 transition-all active:cursor-grabbing',
          isOver
            ? 'scale-[1.03] border-status-livre bg-status-livreFundo shadow-media ring-4 ring-status-livre/30'
            : emArraste
              ? 'animate-pulsoSuave border-dashed border-status-divergencia bg-status-divergenciaFundo shadow-media ring-4 ring-status-divergencia/25'
              : destino.ehNegativo
                ? 'border-status-divergencia/40 bg-status-divergenciaFundo'
                : 'border-primaria/40 bg-primaria-suave',
        )}
      >
        <span
          className={cn(
            'grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-bold text-white',
            isOver
              ? 'bg-status-livre'
              : destino.ehNegativo
                ? 'bg-status-divergencia'
                : 'gradiente-marca',
          )}
        >
          {isOver ? <Check className="h-3.5 w-3.5" /> : 3}
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              'rotulo mb-0.5 flex items-center gap-1.5',
              isOver
                ? 'text-status-livre'
                : destino.ehNegativo
                  ? 'text-status-divergencia'
                  : 'text-primaria',
            )}
          >
            <Crosshair className="h-3 w-3" />
            {isOver
              ? 'pode soltar aqui'
              : emArraste
                ? 'solte aqui, ou em qualquer vaga'
                : destino.ehNegativo
                  ? 'Guardar em · negativo'
                  : 'Guardar em · vaga escolhida'}
          </span>
          <span className="block font-mono text-[14px] font-bold leading-none">
            {destino.endereco}
          </span>
          {previsao !== null ? (
            <span className="tabular mt-1 block text-[11.5px] font-bold text-status-livre">
              vai ficar {numero(destino.saldoSku ?? 0)} → {numero(previsao)} un
            </span>
          ) : (
            <span
              title={DICA_ALCANCE}
              className="mt-1 flex items-center gap-1.5 text-[11px] text-textoFraco"
            >
              <alcanceDestino.Icone className="h-3.5 w-3.5" />
              {alcanceDestino.texto}
            </span>
          )}
          <span className="mt-0.5 block text-[11px] text-textoFraco">
            rua {ruaD} · módulo {gd.modulo} ·{' '}
            {destino.ehNegativo ? (
              <>
                faltam <b className="tabular text-status-divergencia">{numero(faltante)}</b> un
              </>
            ) : destino.saldoPosicao === 0 ? (
              <b className="text-status-livre">vaga livre</b>
            ) : destino.pct !== null ? (
              <>
                vaga <b className="tabular text-texto">{destino.pct}%</b> ocupada
              </>
            ) : (
              'vaga sem medida cadastrada'
            )}
          </span>
        </span>
      </div>

      <span className="flex flex-col justify-center gap-1">
        {trocado ? (
          <Botao variante="fantasma" tamanho="pequeno" onClick={aoVoltarDestino}>
            <Undo2 className="h-3.5 w-3.5" />
            Voltar ao negativo
          </Botao>
        ) : null}
        {origem ? (
          <Botao variante="fantasma" tamanho="pequeno" onClick={aoTrocarOrigem}>
            <Undo2 className="h-3.5 w-3.5" />
            Trocar origem
          </Botao>
        ) : null}
      </span>
    </div>
  );
}

function Passo({
  numero: n,
  rotulo,
  ativo,
  tom,
  children,
}: {
  numero: number;
  rotulo: string;
  ativo: boolean;
  tom: 'origem' | 'neutro';
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div
      className={cn(
        'flex min-w-[215px] flex-1 items-start gap-2.5 rounded-xl border px-3 py-2',
        !ativo && 'border-dashed border-bordaForte bg-superficie',
        ativo && tom === 'origem' && 'border-status-livre/40 bg-status-livreFundo',
        ativo && tom === 'neutro' && 'border-borda bg-superficie',
      )}
    >
      <span
        className={cn(
          'grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-bold',
          ativo ? 'gradiente-marca text-white shadow-marca' : 'bg-superficie2 text-textoSuave',
        )}
      >
        {n}
      </span>
      <span className="min-w-0 flex-1">
        <span className="rotulo mb-0.5 block">{rotulo}</span>
        {children}
      </span>
    </div>
  );
}

/**
 * Quem está sendo movido. O operador confere pelo que vê na mão — foto, nome e
 * EAN — não pelo código interno; e o peso decide se a retirada é manual.
 */
function FichaProduto({ sku, descricao }: { sku: number; descricao: string }): JSX.Element {
  const ficha = useProduto(sku);
  const p = ficha.data;
  const peso = p?.pesoBrutoKg ?? null;
  const volume = p?.volumeM3 ?? null;

  return (
    <>
      <ImagemProduto sku={sku} tamanho={46} arredondada={false} className="rounded-xl" />
      <div className="min-w-0 flex-1">
        <Dialog.Title className="text-[15px] font-bold leading-tight tracking-[-0.01em]">
          Mover estoque no CD — pegar numa vaga, guardar em outra
        </Dialog.Title>
        <Dialog.Description className="truncate text-[12.5px] font-semibold text-texto">
          {p?.nome ?? descricao}
        </Dialog.Description>
        <span className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] text-textoFraco">
          <span>
            SKU <b className="font-mono text-texto">{sku}</b>
          </span>
          {p?.ean ? (
            <span>
              EAN <b className="font-mono text-texto">{p.ean}</b>
            </span>
          ) : null}
          {peso !== null ? (
            <span className="tabular">
              {peso.toLocaleString('pt-BR', { maximumFractionDigits: 3 })} kg/un
            </span>
          ) : null}
          {volume !== null ? (
            <span className="tabular">
              {volume.toLocaleString('pt-BR', { minimumFractionDigits: 3 })} m³/un
            </span>
          ) : null}
          {p && !p.temAtributos ? <span className="italic">sem ficha cadastrada</span> : null}
        </span>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Confirmação: faixa no rodapé, sem diálogo sobre diálogo
// ---------------------------------------------------------------------------

function BarraConfirmacao({
  centro,
  produto,
  origem,
  destino,
  quantidade,
  definirQuantidade,
  aoLimpar,
  aoConcluir,
}: {
  centro: number;
  produto: number;
  origem: Candidata;
  destino: Destino;
  quantidade: string;
  definirQuantidade: (v: string) => void;
  aoLimpar: () => void;
  aoConcluir: () => void;
}): JSX.Element {
  const qtd = Number(quantidade);
  const qtdValida = Number.isInteger(qtd) && qtd > 0;
  const mesmaVaga = origem.localizador === destino.localizador;

  const simulacao = useQuery<Simulacao>({
    queryKey: ['simular', centro, produto, origem.localizador, destino.localizador, qtd],
    queryFn: () =>
      api.post<Simulacao>(
        '/api/movimentacao/simular',
        { produto, origem: origem.localizador, destino: destino.localizador, quantidade: qtd },
        { centro },
      ),
    enabled: qtdValida && !mesmaVaga,
  });

  const mover = useMutation({
    mutationFn: () =>
      api.post('/api/movimentacao', {
        centro,
        produto,
        quantidade: qtd,
        origem: origem.localizador,
        destino: destino.localizador,
      }),
    onSuccess: aoConcluir,
  });

  const s = simulacao.data;

  return (
    <div className="border-t border-borda bg-superficie px-4 py-3">
      <div className="flex flex-wrap items-end gap-4">
        <span className="leading-tight">
          <span className="rotulo block">Pegar em</span>
          <span className="font-mono text-[14px] font-bold text-status-livre">
            {origem.endereco}
          </span>
        </span>
        <ArrowRight className="mb-1 h-4 w-4 text-textoSuave" />
        <span className="leading-tight">
          <span className="rotulo block">Guardar em</span>
          <span
            className={cn(
              'font-mono text-[14px] font-bold',
              destino.ehNegativo ? 'text-status-divergencia' : 'text-primaria',
            )}
          >
            {destino.endereco}
          </span>
        </span>

        <label className="block">
          <span className="rotulo mb-1 block">Quantas unidades levar</span>
          <Campo
            value={quantidade}
            onChange={(e) => definirQuantidade(e.target.value.replace(/\D/g, ''))}
            inputMode="numeric"
            className="tabular w-[110px] text-[16px] font-bold"
            aria-label="Quantidade a transferir"
          />
        </label>

        {simulacao.isLoading ? (
          <span className="mb-2 flex items-center gap-2 text-[12px] text-textoFraco">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            conferindo…
          </span>
        ) : null}

        {s?.ok ? (
          <span className="tabular mb-1.5 text-[12px] text-textoFraco">
            saldo do SKU no destino: <b className="text-texto">{numero(s.saldoDestinoAntes)}</b> →{' '}
            <b className="text-texto">{numero(s.saldoDestinoDepois)}</b>
          </span>
        ) : null}

        <div className="ml-auto flex items-center gap-2">
          <Botao variante="fantasma" onClick={aoLimpar}>
            <Undo2 className="h-4 w-4" />
            Trocar origem
          </Botao>
          <Botao
            variante="primaria"
            disabled={!qtdValida || mesmaVaga || !s?.ok}
            carregando={mover.isPending}
            onClick={() => mover.mutate()}
          >
            {!mover.isPending ? <Check className="h-4 w-4" /> : null}
            Confirmar transferência
          </Botao>
        </div>
      </div>

      {mesmaVaga ? (
        <p className="mt-2 flex items-start gap-2 rounded-lg border border-status-atencao/30 bg-status-atencaoFundo px-3 py-1.5 text-[12px] text-textoFraco">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-atencao" />
          Origem e destino são a mesma vaga. Escolha outra ponta.
        </p>
      ) : null}

      {s?.impedimentos.map((m) => (
        <p
          key={m}
          className="mt-2 flex items-start gap-2 rounded-lg border border-status-divergencia/30 bg-status-divergenciaFundo px-3 py-1.5 text-[12px] text-status-divergencia"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {m}
        </p>
      ))}
      {s?.avisos.map((m) => (
        <p
          key={m}
          className="mt-2 flex items-start gap-2 rounded-lg border border-status-atencao/30 bg-status-atencaoFundo px-3 py-1.5 text-[12px] text-textoFraco"
        >
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-atencao" />
          {m}
        </p>
      ))}
      {mover.isError ? <EstadoErro erro={mover.error} className="py-2" /> : null}

      <p className="mt-2 text-[11px] text-textoFraco">
        Ao confirmar, o Kardex recebe duas linhas: <b>saída</b> em {comoEndereco(origem.endereco)}{' '}
        e <b>entrada</b> em {comoEndereco(destino.endereco)}, mesmo SKU e mesma quantidade, no
        centro {centro}.
      </p>
    </div>
  );
}
